import {
  clientsTasksCreateMutation,
  PaginatedReturnUserListReadable,
  PaginatedTaskTypeListReadable,
  ReturnUserReadable,
  TaskTypeReadable,
  TaskTypesListData,
  taskTypesListInfiniteOptions,
  UsersListData,
  usersListInfiniteOptions
} from '@crm/api'
import { InfiniteSelect } from '@crm/components'
import { TaskStatus } from '@crm/features/tasks/components'
import {
  formatToIsoString,
  listTransformResults,
  TagPriorityByValue,
  userTransformResults
} from '@crm/utils'
import { useSessionContext } from '@shared/hooks'
import { useLocales } from '@shared/locales'
import {
  DATE_TIME_FORMAT,
  DATE_TIME_FORMAT_API,
  format,
  showApiErrors,
  tomorrow
} from '@shared/utils'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { App, Col, DatePicker, Form, Input, Modal, Radio, Row, Tag } from 'antd'
import { ModalProps } from 'antd/lib'
import { useEffect } from 'react'
import { useParams } from 'react-router-dom'

const formName = 'add-client-task-form'

type Props = {
  onCancel: () => void
} & ModalProps

export const AddClientTask = ({ onCancel, ...props }: Props) => {
  const { t } = useLocales()
  const { id: clientId } = useParams()
  const [form] = Form.useForm()
  const { user } = useSessionContext()
  const { message } = App.useApp()
  const queryClient = useQueryClient()

  useEffect(() => {
    form.setFieldsValue({ owner: user?.user?.sso_id })
  }, [form, user?.user])

  const { mutate: createMutate, isPending } = useMutation({
    ...clientsTasksCreateMutation(),
    onSuccess: () => {
      queryClient.refetchQueries()
      onCancel()
      message.success(t('success.success_data_save'))
    },
    onError: (e) => showApiErrors(e, form)
  })

  const onSubmit = async () => {
    const values = await form.validateFields()
    const { deadline, type, status, ...rest } = values

    createMutate({
      path: { client_id: clientId! },
      body: {
        ...rest,
        client: Number(clientId),
        type,
        status: status ?? null,
        deadline:
          deadline &&
          formatToIsoString(
            format(deadline, DATE_TIME_FORMAT_API, DATE_TIME_FORMAT)
          )
      }
    })
  }

  return (
    <Modal
      width={800}
      maskClosable={false}
      title={t('lead.task')}
      cancelText={t('buttons.cancel')}
      okText={t('buttons.save')}
      destroyOnHidden
      okButtonProps={{ htmlType: 'submit', form: formName, loading: isPending }}
      onCancel={onCancel}
      {...props}
    >
      <Form
        form={form}
        name={formName}
        onFinish={onSubmit}
        layout="vertical"
        initialValues={{
          priority: 2,
          deadline: tomorrow
        }}
      >
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item
              name="title"
              label={t('task.title')}
              rules={[{ required: true }]}
            >
              <Input placeholder={t('task.title')} />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item name="type" label={t('task.task_type')}>
              <InfiniteSelect<
                TaskTypesListData,
                PaginatedTaskTypeListReadable,
                TaskTypeReadable
              >
                className="w-full"
                popupMatchSelectWidth
                placeholder={t('task.task_type')}
                getQueryFunction={taskTypesListInfiniteOptions}
                getOptions={({ options }) => listTransformResults(options)}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col span={24}>
            <Form.Item name="description" label={t('task.description')}>
              <Input.TextArea rows={4} placeholder={t('task.description')} />
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col span={24}>
            <Form.Item name="owner" label={t('task.assign_to')}>
              <InfiniteSelect<
                UsersListData,
                PaginatedReturnUserListReadable,
                ReturnUserReadable
              >
                className="w-full"
                popupMatchSelectWidth
                placeholder={t('main.select')}
                getQueryFunction={usersListInfiniteOptions}
                getOptions={({ options }) => userTransformResults(options)}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="priority" label={t('task.priority')}>
              <Radio.Group
                size="small"
                options={Object.entries(TagPriorityByValue(t)).map(
                  ([key, value]) => ({
                    label: <Tag color={value.color}>{value.title}</Tag>,
                    value: Number(key)
                  })
                )}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item name="deadline" label={t('filter.deadline')}>
              <DatePicker
                placement="bottomRight"
                showTime
                format={DATE_TIME_FORMAT}
                className="w-full"
              />
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col span={24}>
            <Form.Item name="status" label={t('nomenclature.status')}>
              <TaskStatus />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  )
}
